import React from 'react';
import { Box, Group } from '@mantine/core';
import { useForm } from '@mantine/hooks';
import { useNotifications } from '@mantine/notifications';
import { sendPasswordResetEmail } from 'firebase/auth';

import { useIsMobileMedia, useRequestStates } from '~/hooks';
import { authContextSlice, useAuthContext } from '~/pages/LandingPage/contexts/AuthContext';
import { LoaderWithOverlay } from '~/components/molecules';
import { getFirebaseAuth } from '~/lib/firebase';

import { LabelMedium, LabelMediumStrong, LabelSmallStrong, ParagraphMedium, ParagraphSmall } from '../../../typography';
import * as Styles from '../AuthModal.styled';

const NOTIFICATION_TIMEOUT = 5000;
const ForgotPasswordView = () => {
  const isMobileMedia = useIsMobileMedia();

  const { showNotification } = useNotifications();

  const { dispatchAuthCtx } = useAuthContext();

  const [resetPasswordReq, resetPasswordReqHandler] = useRequestStates();

  const form = useForm({
    initialValues: { email: '' },
  });

  const handleLoginClick = () => {
    dispatchAuthCtx(authContextSlice.actions.setView('login'));
  };

  const handleSubmit = async () => {
    const email = form.values.email;
    if (!email) {
      form.setFieldError('email', 'Please enter your email address');
      return;
    }

    const lastSentTimestamp = resetPasswordReq.data?.lastSentTimestamp;
    const elapsedSeconds = Math.ceil((Date.now() - lastSentTimestamp) / 1000);
    if (elapsedSeconds < 60) {
      showNotification({ color: 'orange', title: `Please try again after ${60 - elapsedSeconds} seconds` });
      return;
    }

    resetPasswordReqHandler.setPending();
    const auth = getFirebaseAuth();
    let err;
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (e) {
      err = e;
    }

    if (!err || err.code === 'auth/user-not-found') {
      resetPasswordReqHandler.setFulfilled({ lastSentTimestamp: Date.now() });
      showNotification({
        color: 'teal',
        title: 'Please check your email',
        message:
          'Instructions to reset your password will be sent to you. Please check your email inbox or spam folder.',
        autoClose: NOTIFICATION_TIMEOUT,
      });
    } else {
      resetPasswordReqHandler.setRejected(err);
      showNotification({
        color: 'red',
        title: 'Something went wrong!',
        message: 'Failed to send password reset instructions',
        autoClose: NOTIFICATION_TIMEOUT,
      });
    }
  };

  return (
    <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
      <LoaderWithOverlay visible={resetPasswordReq.isPending} />
      <ParagraphMedium mt={isMobileMedia ? '24px' : '32px'} color="#434343">
        Enter the email address you used when you joined and we'll send you instructions to reset your password.
      </ParagraphMedium>

      <Box mt={isMobileMedia ? '16px' : '24px'}>
        <Styles.FormTextInput
          label={<LabelMedium>E-mail</LabelMedium>}
          type="email"
          placeholder="E.g. rohan@company.com"
          {...form.getInputProps('email')}
        />
      </Box>
      <Box mt={isMobileMedia ? '24px' : '32px'}>
        <Styles.PrimaryBtn fullWidth type="submit">
          <LabelMediumStrong color="#fff">Send Reset Instructions</LabelMediumStrong>
        </Styles.PrimaryBtn>
      </Box>
      <Group mt="12px" spacing="4px">
        <ParagraphSmall>Already have an account?</ParagraphSmall>
        <LabelSmallStrong color="#1F7AE0" onClick={handleLoginClick} style={{ cursor: 'pointer' }}>
          Log In
        </LabelSmallStrong>
      </Group>
    </Box>
  );
};

export default ForgotPasswordView;
